/**
 * SupplierInvoicesPage — COM-04: Facturas de Proveedor
 */
import { useState, useRef, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { fmtDate } from '@/lib/utils'
import { formatMoneyForTenant, useTaxIdLabel, useTenantLocale } from '@/lib/locale'
import { FileText, Plus, Loader2, CheckCircle, XCircle, Upload, Download, Clock, FileDown, AlertTriangle, Trash2, FilePenLine } from 'lucide-react'
import { KebabMenu } from '@/components/KebabMenu'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import {
  useSupplierInvoices, useSupplierInvoice, useCreateSupplierInvoice,
  useApproveSupplierInvoice, useCancelSupplierInvoice, useDeleteSupplierInvoice,
  useUpdateSupplierInvoice, useResponderSiiSinXml,
  useUploadXMLInvoice, useTriggerRCVDownload, nonBillableAmount, payableTotal,
  type SupplierInvoiceListItem, type SupplierInvoiceStatus,
} from '../hooks/usePurchases'
import { EditIntlInvoiceModal } from './EditIntlInvoiceModal'
import { ToastContainer, useToast } from '@/components/Toast'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { useAcceptDTE, useAcuseReciboDTE, useRejectDTE } from '@/modules/dte-inbound/hooks/useInboundDTE'
import { AcuseSiiModal, type RespuestaSii } from '@/modules/dte-inbound/components/AcuseSiiModal'
import { useCompanyProfile } from '@/modules/config/hooks/useConfig'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { extractErrorMessage } from '@/lib/errorUtils'
import { PageHeader } from '@/components/HelpPanel'
import { PageSizeSelector, DEFAULT_PAGE_SIZE } from '@/components/PageSizeSelector'
import { HELP } from '@/data/helpContent'
import { SupplierInvoiceModal } from './SupplierInvoiceModal'
import { SupplierInvoiceDetailModal } from './SupplierInvoiceDetailModal'
import { ApproveWithAccountModal } from '@/modules/accounting/components/ApproveWithAccountModal'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { FilterBar, useFilters, type FilterConfig } from '@/components/filters'
import {
  type SortState,
  sortStateToParam,
  loadSortFromStorage,
  saveSortToStorage,
} from '@/components/SortableHeader'
import { DataTable } from '@/components/ds/DataTable'
import { ordenControlado } from '@/components/ds/ordenControlado'
import { usePermission } from '@/hooks/usePermission'
import { StatusBadge as DsStatusBadge } from '../../../components/ds/StatusBadge'
import { PAYABLE_STATUS } from '../../../components/ds/statusCatalogs'
import { Pagination } from '@/components/ds/Pagination'

const STATUS_CONFIG: Record<SupplierInvoiceStatus, { label: string; cls: string; dotCls: string }> = {
  rcv_pending:   { label: 'Pendiente XML', cls: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',  dotCls: 'bg-purple-400' },
  rcv_reclamado: { label: 'Reclamado',     cls: 'bg-danger-surface text-danger-content',        dotCls: 'bg-danger-solid' },
  registered:    { label: 'Registrada',    cls: 'bg-surface-muted text-content-muted',   dotCls: 'bg-content-subtle' },
  approved:      { label: 'Aprobada',      cls: 'bg-warning-surface text-warning-content',    dotCls: 'bg-warning-solid' },
  paid:          { label: 'Pagada',        cls: 'bg-success-surface text-success-content',dotCls: 'bg-emerald-400' },
  cancelled:     { label: 'Anulada',       cls: 'bg-surface-muted text-content-muted',   dotCls: 'bg-content-subtle' },
}


// Chip de advertencia compacto (solo ícono + tooltip) para descongestionar la
// columna de estado. Tono ámbar (aviso) o rojo (problema).
function WarnChip({ title, tone = 'amber' }: { title: string; tone?: 'amber' | 'red' }) {
  const cls = tone === 'red'
    ? 'text-danger-content-soft bg-danger-surface'
    : 'text-warning-content-soft bg-warning-surface'
  return (
    <span title={title} className={`inline-flex items-center justify-center w-5 h-5 rounded-full cursor-help ${cls}`}>
      <AlertTriangle size={11} />
    </span>
  )
}

function DeadlineBadge({ days }: { days: number | null | undefined }) {
  if (days == null) return null
  // Plazo de acuse/reclamo (Ley 19.983, 8 días). Si ya pasó, opera el silencio
  // positivo (el DTE se entiende aceptado) → no mostrar "Vencido" (alarmaba sin
  // motivo). No mostramos nada cuando el plazo ya transcurrió.
  if (days < 0) return null
  if (days <= 2) return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
      <Clock size={10} />{days}d
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary">
      <Clock size={10} />{days}d
    </span>
  )
}

function StatusBadge({ status }: { status: SupplierInvoiceStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.registered
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

const AP_ADJ_BADGE_CFG = {
  adjusted:                { label: 'Ajustado',            cls: 'bg-warning-surface text-warning-content', dotCls: 'bg-warning-solid' },
  annulled:                { label: 'Anulado',             cls: 'bg-danger-surface text-danger-content',     dotCls: 'bg-danger-solid' },
  annulled_after_payment:  { label: 'Anulado (crédito)',   cls: 'bg-danger-surface text-danger-content',     dotCls: 'bg-danger-solid' },
} as const

function APAdjustmentBadge({ invoice }: { invoice: SupplierInvoiceListItem }) {
  const s = invoice.adjustment_status
  if (!s || s === 'normal') return null
  const cfg = AP_ADJ_BADGE_CFG[s]
  const adjs = invoice.adjustments ?? []
  const tooltipLines = adjs.map(a =>
    `${a.type} ${a.document_type_sii} #${a.folio} — ${formatMoneyForTenant(a.amount)}${a.reason ? ` (${a.reason})` : ''}`
  ).join('\n') || cfg.label
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${cfg.cls}`}
      title={`${cfg.label}\n\n${tooltipLines}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}


function PaymentBadge({ invoice }: { invoice: SupplierInvoiceListItem }) {
  // Una factura anulada por NC de proveedor queda con saldo 0: mostrar "Anulada"
  // en vez de "Pagada" (no se pagó, se anuló) — mismo criterio que Ventas.
  const adj = invoice.adjustment_status
  if (adj === 'annulled' || adj === 'annulled_after_payment') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-surface-muted text-content-muted" title="Saldo anulado por Nota de Crédito">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-content-subtle" />
        Anulada
      </span>
    )
  }
  // NC (61/112) y facturas anuladas no se pagan → sin estado de pago.
  const sii = invoice.document_type_sii ?? 0
  if (sii === 61 || sii === 112 || invoice.status === 'cancelled') {
    return <span className="text-xs text-content-subtle">—</span>
  }
  // Homologa "sin valor" = Pendiente: una factura sin estado de pago persistido
  // está pendiente de pago (el campo solo se setea en eventos de pago).
  const status = invoice.payment_status || 'pending'
  return (
    <DsStatusBadge
      status={status}
      catalog={PAYABLE_STATUS}
      label={PAYABLE_STATUS[status] ? undefined : (invoice.payment_status_display || status)}
    />
  )
}

export function SupplierInvoicesPage() {
  const locale = useTenantLocale()
  const taxLabel = useTaxIdLabel()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [modalOpen, setModalOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [approveTargetId, setApproveTargetId] = useState<string | null>(null)
  const [approveFromDetail, setApproveFromDetail] = useState(false)

  // Auto-open detail from ?highlight= param (from voucher navigation)
  useEffect(() => {
    const hl = searchParams.get('highlight')
    if (hl) {
      setDetailId(hl)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])
  const [apiError, setApiError] = useState<string | null>(null)
  const [xmlSuccess, setXmlSuccess] = useState<string | null>(null)
  // Cooldown del botón "Descargar RCV": evita doble clic mientras se encola la tarea.
  const [rcvCooldown, setRcvCooldown] = useState(false)

  const handleTriggerRCV = () => {
    setApiError(null)
    // Feedback inmediato: la descarga es asíncrona (tarea en background), el
    // resultado llega minutos después. Avisamos al instante que ya está en curso.
    setXmlSuccess('Descarga del RCV en proceso… Los resultados aparecerán en unos minutos.')
    setRcvCooldown(true)
    setTimeout(() => setRcvCooldown(false), 8000)
    triggerRCV.mutate(undefined, {
      onSuccess: () => setXmlSuccess('Descarga del RCV iniciada correctamente. Los resultados aparecerán en unos minutos.'),
      onError: (e: unknown) => {
        setXmlSuccess(null)
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'No se pudo iniciar la descarga del RCV. Intenta nuevamente.')
      },
    })
  }
  const { can } = usePermission()
  const canCreate = can('purchases.invoices', 'create')
  const canDelete = can('purchases.invoices', 'delete')
  const canApprove = can('purchases.invoices', 'approve')
  const canEdit = can('purchases.invoices', 'edit')
  const { data: locConfig } = useLocalizationConfig()
  const allowApproveWithoutXml = !!locConfig?.allow_approve_without_xml
  const navigate = useNavigate()
  // Con la Vista 360 de compras activa, abrir una factura ES el 360 (la lista
  // colapsa al rail). Sin el flag, abre el modal de detalle clásico.
  const openInvoice = (invId: string) => {
    if (locConfig?.ui_purchases_360) navigate(`/purchases/invoices/${invId}/360`)
    else setDetailId(invId)
  }
  const xmlInputRef = useRef<HTMLInputElement>(null)

  const filterConfig: FilterConfig = useMemo(() => [
    // Quick: lo de uso diario. El resto va al cajón "Filtros" (advanced).
    { key: 'search', type: 'search' as const, placement: 'quick' as const,
      placeholder: `Buscar: proveedor, ${taxLabel}, N° factura, notas, monto (>100000, 1000-2000)…` },
    { key: 'status', type: 'select' as const, label: 'Estado', placement: 'quick' as const,
      options: [
        { value: '', label: 'Todas' },
        { value: 'rcv_pending', label: 'Pendiente XML' },
        { value: 'rcv_reclamado', label: 'Reclamada' },
        { value: 'registered', label: 'Registrada' },
        { value: 'approved', label: 'Aprobada' },
        { value: 'paid', label: 'Pagada' },
        { value: 'cancelled', label: 'Anulada' },
      ] },
    { key: 'document_type_sii', type: 'select' as const, label: 'Tipo de documento', placement: 'quick' as const,
      widthClass: 'w-56',
      options: [
        { value: '', label: 'Todos' },
        { value: '33', label: '33 — Factura Electrónica' },
        { value: '34', label: '34 — Factura Exenta' },
        { value: '46', label: '46 — Factura de Compra' },
        { value: '56', label: '56 — Nota de Débito' },
        { value: '61', label: '61 — Nota de Crédito' },
        { value: '52', label: '52 — Guía de Despacho' },
      ] },
    { key: 'date', type: 'date-range' as const, label: 'Fecha emisión', placement: 'quick' as const, defaultPreset: 'current-month' as const },
    // Estado de pago queda inline (se eliminó el cajón "Filtros"); el resto de
    // filtros avanzados se retiró — la búsqueda por monto vive en el buscador
    // (>100000, 100000-200000, monto:150000).
    { key: 'payment_status', type: 'select' as const, label: 'Estado de pago', placement: 'quick' as const,
      options: [
        { value: '', label: 'Todos' },
        { value: 'paid', label: 'Pagada' },
        { value: 'partial', label: 'Parcial' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'overdue', label: 'Vencida' },
      ] },
  ], [taxLabel])

  // Ayuda con la sintaxis del buscador, localizada según el identificador del país.
  const helpContent = useMemo(() => {
    const base = HELP['purchases.invoices']
    const idLower = taxLabel.toLowerCase()
    return {
      ...base,
      sections: [
        ...base.sections,
        {
          icon: 'tip' as const,
          title: 'Búsqueda',
          content:
            `Busca por proveedor, ${taxLabel}, N° de factura o notas.\n\n` +
            `**Búsqueda avanzada:**\n` +
            `• **${idLower}:76123456** — filtra por ${taxLabel}\n` +
            `• **>100000** o **<50000** — por monto (mayor / menor que)\n` +
            `• **100000-200000** — rango de monto\n` +
            `• **monto:150000** — total exacto\n` +
            `• Un número solo busca N° de factura y monto exacto.`,
        },
        {
          icon: 'info' as const,
          title: 'Filtros',
          content:
            'Los filtros están todos a la vista (se quitó el panel lateral): Estado, ' +
            'Tipo de documento, Fecha de emisión y Estado de pago. Para acotar por ' +
            'monto usa el buscador con los operadores de arriba.',
        },
      ],
    }
  }, [taxLabel])

  // persistKey: preserva los filtros del listado al entrar a la Vista 360 y volver.
  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1), persistKey: 'purchases-invoices' })
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('supplier-invoices-sort', { field: 'issue_date', direction: 'desc' }),
  )
  const handleSortChange = (next: SortState) => {
    setSort(next)
    saveSortToStorage('supplier-invoices-sort', next)
    setPage(1)
  }

  // Filtros efectivos del listado (sin paginación). Se reutilizan para el fetch
  // y se persisten para que el rail de la Vista 360 muestre EXACTAMENTE el mismo
  // conjunto filtrado/ordenado que el usuario venía viendo.
  const listParams = useMemo(() => ({
    ...filters.params,
    ordering: sortStateToParam(sort),
  }), [filters.params, sort])

  // Persistir los filtros efectivos para el rail de la Vista 360: así arranca con
  // el MISMO conjunto (búsqueda/estado/tipo/orden) en vez de traer todas las
  // facturas recientes. Se limpian las claves vacías.
  useEffect(() => {
    try {
      const clean = Object.fromEntries(
        Object.entries(listParams).filter(([, v]) => v != null && v !== ''),
      )
      sessionStorage.setItem('purchases-invoices:rail-params', JSON.stringify(clean))
    } catch { /* ignore */ }
  }, [listParams])

  const { data, isLoading } = useSupplierInvoices({ ...listParams, page, page_size: pageSize })

  const { data: detailInvoice } = useSupplierInvoice(detailId)
  const createInvoice = useCreateSupplierInvoice()
  const approve = useApproveSupplierInvoice()
  const cancelInvoice = useCancelSupplierInvoice()
  const deleteInvoice = useDeleteSupplierInvoice()
  const uploadXML = useUploadXMLInvoice()
  const triggerRCV = useTriggerRCVDownload()
  const { confirm: confirmCancel, modalProps: cancelModalProps } = useConfirmModal()
  const { confirm: confirmDelete, modalProps: deleteModalProps } = useConfirmModal()

  // Editar una factura INTERNACIONAL (monto/líneas/fechas) desde el listado.
  // El backend lo permite en cualquier estado y recontabiliza el comprobante
  // (no son DTE del SII), pero el botón vivía SOLO en la Vista 360, que está
  // detrás de `ui_purchases_360` — apagado en los 3 tenants de prod, así que la
  // acción era inalcanzable por UI. Los ajustes posteriores de un proveedor
  // (Freshworks) son justamente el caso de uso.
  const [editIntlId, setEditIntlId] = useState<string | null>(null)
  const { data: editIntlInvoice } = useSupplierInvoice(editIntlId)
  const updateIntlInvoice = useUpdateSupplierInvoice(editIntlId ?? '')
  const toast = useToast()

  // Acuse/Reclamo SII (Ley 19.983) sobre el DTE recibido vinculado (#432 PR3).
  // Reusa los endpoints/hooks de dte-inbound; gateado por dte_inbound.approve.
  const qc = useQueryClient()
  const canAcuseSii = can('dte_inbound', 'approve')
  const acuseSii = useAcuseReciboDTE()
  const acceptSii = useAcceptDTE()
  const rejectSii = useRejectDTE()
  // Sin XML (#1637): la misma respuesta, registrada sobre la factura del RCV.
  const responderSinXml = useResponderSiiSinXml()
  const respondeSinXml = (inv: SupplierInvoiceListItem) =>
    canAcuseSii && !!inv.sii_acuse?.sin_xml && !!(inv.sii_acuse.can_acuse || inv.sii_acuse.can_accept)
  const { data: companyProfile } = useCompanyProfile()
  // 🔴 Antes esta pantalla solo tenia `useAcceptDTE` (ACD) detras de un modal
  // que decia «acuse de recibo». El acuse es el ERM y era inalcanzable desde
  // aca: medido en prod, 10 documentos quedaron con ACD y sin ERM, o sea
  // pendientes en el RCV sin que nadie lo supiera.
  const [respuestaSii, setRespuestaSii] = useState<
    { id: string | null; invoiceId: string; label: string; modo: 'aceptar' | 'reclamar' } | null
  >(null)
  const refreshAfterAcuse = () => {
    qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    setDetailId(null)
  }

  const requestAcceptSii = (inboundDteId: string | null, label: string, invoiceId: string) =>
    setRespuestaSii({ id: inboundDteId, invoiceId, label: `Factura ${label}`, modo: 'aceptar' })

  const requestRejectSii = (inboundDteId: string | null, label: string, invoiceId: string) =>
    setRespuestaSii({ id: inboundDteId, invoiceId, label: `Factura ${label}`, modo: 'reclamar' })

  /** Despacha la accion elegida en el modal. Cada una es un evento distinto
   *  ante el SII: el ERM despeja el RCV, el ACD no, y los tres reclamos son
   *  excluyentes con ambos. */
  const confirmarRespuestaSii = (r: RespuestaSii) => {
    if (!respuestaSii) return
    const done = { onSuccess: () => { refreshAfterAcuse(); setRespuestaSii(null) } }
    if (respuestaSii.id === null) {
      // Sin XML: el SII responde en el momento, y si rechaza (contado, plazo
      // vencido) su motivo es lo que hay que mostrar.
      responderSinXml.mutate(
        { id: respuestaSii.invoiceId, accion: r.accion, observaciones: r.observaciones },
        {
          onSuccess: () => {
            toast.success(r.accion === 'ERM' || r.accion === 'ACD'
              ? 'Respuesta registrada en el SII.' : 'Reclamo registrado en el SII.')
            refreshAfterAcuse(); setRespuestaSii(null)
          },
          onError: e => toast.error(extractErrorMessage(e, 'El SII no registró la respuesta.')),
        },
      )
      return
    }
    if (r.accion === 'ERM') {
      acuseSii.mutate({ id: respuestaSii.id, recinto: r.recinto, observaciones: r.observaciones }, done)
    } else if (r.accion === 'ACD') {
      acceptSii.mutate({ id: respuestaSii.id, observaciones: r.observaciones }, done)
    } else {
      rejectSii.mutate({ id: respuestaSii.id, reason: r.accion, observaciones: r.observaciones }, done)
    }
  }

  // El backend responde 409 VOUCHER_APPROVED cuando la factura tiene un asiento
  // firme sin reversa. En ese caso ofrecemos el paso que faltaba —generar el
  // asiento de reversión— en vez de dejar al usuario en un callejón sin salida:
  // el comprobante aprobado NO se puede eliminar, así que "elimina el
  // comprobante primero" nunca tuvo forma de cumplirse.
  const voucherBlocked = (e: unknown) => {
    const err = e as {
      response?: { status?: number; data?: { error?: { code?: string; message?: string } } }
    }
    const error = err.response?.data?.error
    return error?.code === 'VOUCHER_APPROVED' ? (error.message ?? '') : null
  }

  // Segunda confirmación: revertir el comprobante (asiento de reversión, conserva
  // la traza) y completar la acción en el mismo paso.
  const offerReversal = (
    accion: 'anular' | 'eliminar', id: string, label: string,
    backendMsg: string, onDone?: () => void,
  ) => confirmCancel({
    message: `${backendMsg}\n\n¿Generar el asiento de reversión y ${accion} la factura ${label}? La reversión deja el asiento sin efecto (invierte DEBE↔HABER) y queda registrada en el Libro Diario.`,
    variant: 'danger',
    confirmLabel: `Revertir el comprobante y ${accion}`,
    onConfirm: () => new Promise<void>(resolve => {
      setApiError(null)
      const mutation = accion === 'anular' ? cancelInvoice : deleteInvoice
      mutation.mutate({ id, reverseVoucher: true }, {
        onSuccess: () => { onDone?.(); resolve() },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? `No se pudo ${accion} la factura.`)
          resolve()
        },
      })
    }),
  })

  // Anular es destructivo (revierte el comprobante contable y no se puede
  // deshacer) — siempre pedir confirmación antes de mutar.
  const requestCancel = (id: string, label: string, onDone?: () => void) =>
    confirmCancel({
      message: `¿Anular la factura ${label}? Esta acción no se puede deshacer.`,
      variant: 'danger',
      confirmLabel: 'Anular factura',
      onConfirm: () => new Promise<void>(resolve => {
        cancelInvoice.mutate({ id }, {
          onSuccess: () => { onDone?.(); resolve() },
          onError: (e: unknown) => {
            const msg = voucherBlocked(e)
            if (msg) offerReversal('anular', id, label, msg, onDone)
            resolve()
          },
        })
      }),
    })

  // Eliminar es un borrado DEFINITIVO (no deja traza). Solo seguro si la factura
  // no está contabilizada, sin pagos ni NC/ND; el backend valida y devuelve un
  // mensaje claro (400/409) que mostramos en el banner si no se puede.
  const requestDelete = (id: string, label: string, onDone?: () => void) =>
    confirmDelete({
      message: `¿Eliminar definitivamente la factura ${label}? Esta acción no se puede deshacer y el registro desaparece. Si proviene de un DTE recibido, este vuelve a quedar pendiente por importar.`,
      variant: 'danger',
      confirmLabel: 'Eliminar factura',
      onConfirm: () => new Promise<void>(resolve => {
        setApiError(null)
        deleteInvoice.mutate({ id }, {
          onSuccess: () => { onDone?.(); resolve() },
          onError: (e: unknown) => {
            const msg = voucherBlocked(e)
            if (msg) offerReversal('eliminar', id, label, msg, onDone)
            else {
              const err = e as { response?: { data?: { error?: { message?: string } } } }
              setApiError(err.response?.data?.error?.message ?? 'No se pudo eliminar la factura.')
            }
            resolve()
          },
        })
      }),
    })

  const handleSaveIntl = (formData: Record<string, unknown>) => {
    setApiError(null)
    updateIntlInvoice.mutate(formData as Parameters<typeof updateIntlInvoice.mutate>[0], {
      onSuccess: (saved: unknown) => {
        setEditIntlId(null)
        // El backend guarda los montos aunque falle la regeneración del
        // comprobante, y avisa por este campo. Sin mostrarlo, la factura queda
        // editada y sin asiento mientras el usuario cree que todo salió bien.
        const w = (saved as { accounting_warning?: string } | undefined)?.accounting_warning
        if (w) toast.warning(w)
        else toast.success('Factura actualizada.')
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'No se pudo guardar la factura.')
      },
    })
  }

  const handleSave = (formData: Record<string, unknown>) => {
    setApiError(null)
    createInvoice.mutate(formData as Parameters<typeof createInvoice.mutate>[0], {
      onSuccess: () => setModalOpen(false),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al registrar')
      },
    })
  }

  const handleXMLUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setApiError(null)
    setXmlSuccess(null)
    uploadXML.mutate(file, {
      onSuccess: (inv) => {
        setXmlSuccess(`Factura ${inv.invoice_number} cargada desde XML correctamente.`)
        if (xmlInputRef.current) xmlInputRef.current.value = ''
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al cargar el XML')
        if (xmlInputRef.current) xmlInputRef.current.value = ''
      },
    })
  }

  const fmtAmount = (v: string) =>
    formatMoneyForTenant(v)

  return (
    <div data-tour="purchases-page" className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Facturas de Proveedor"
        subtitle={`${data?.count ?? 0} factura${data?.count !== 1 ? 's' : ''}`}
        help={helpContent}
      >
        {/* Botón Descargar RCV */}
        <button
          data-tour="purchases-rcv"
          onClick={handleTriggerRCV}
          disabled={triggerRCV.isPending || rcvCooldown}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 text-sm rounded-lg transition-colors disabled:opacity-50">
          {triggerRCV.isPending || rcvCooldown
            ? <><Loader2 size={14} className="animate-spin" />Iniciando descarga…</>
            : <><Download size={14} />Descargar RCV</>
          }
        </button>
        {/* Botón Cargar XML */}
        <input
          ref={xmlInputRef}
          type="file"
          accept=".xml"
          className="hidden"
          onChange={handleXMLUpload}
        />
        <button
          data-tour="purchases-xml"
          onClick={() => xmlInputRef.current?.click()}
          disabled={uploadXML.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-surface hover:bg-hover text-content border border-divider-strong text-sm rounded-lg transition-colors disabled:opacity-50">
          {uploadXML.isPending
            ? <><Loader2 size={14} className="animate-spin" />Cargando...</>
            : <><Upload size={14} />Cargar XML</>
          }
        </button>
        {canCreate && (
          <button
            data-tour="purchases-new"
            onClick={() => { setApiError(null); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={15} />Nueva Factura
          </button>
        )}
      </PageHeader>

      {/* Mensajes de feedback (RCV / XML) */}
      {xmlSuccess && (
        <div className="mb-4 p-3 rounded-lg bg-success-surface border border-success-border text-success-content text-sm flex items-center gap-2">
          <CheckCircle size={15} />{xmlSuccess}
        </div>
      )}
      {apiError && (
        <div className="mb-4 p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm flex items-center gap-2">
          <AlertTriangle size={15} />{apiError}
        </div>
      )}

      {/* Filtros compactos (sin cajón lateral: todo inline) */}
      <div data-tour="purchases-filters">
        <FilterBar config={filterConfig} filters={filters} labelLayout="stacked" />
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={9} />
        ) : !data?.invoices.length ? (
          <EmptyState icon={FileText} size="md" title="No hay facturas registradas" />
        ) : (
          <DataTable
            columns={[
              {
                key: 'invoice_number', header: 'Nro. Factura', sortable: true,
                render: (inv: SupplierInvoiceListItem) => <span className="font-medium text-content whitespace-nowrap">{inv.invoice_number}</span>,
              },
              {
                key: 'supplier_name', header: 'Proveedor',
                render: (inv: SupplierInvoiceListItem) => (
                  <span className="block max-w-[14rem] truncate" title={inv.supplier_name}>{inv.supplier_name}</span>
                ),
              },
              {
                key: 'issue_date', header: 'Emisión', sortable: true,
                render: (inv: SupplierInvoiceListItem) => <span className="whitespace-nowrap">{fmtDate(inv.issue_date, locale)}</span>,
              },
              {
                key: 'due_date', header: 'Vcto.', sortable: true,
                render: (inv: SupplierInvoiceListItem) => <span className="whitespace-nowrap">{inv.due_date ? fmtDate(inv.due_date, locale) : '—'}</span>,
              },
              {
                key: 'status', header: 'Estado',
                // Estado simplificado: badge principal + ajuste NC/ND. El resto
                // (avisos puntuales) van como íconos compactos con tooltip. El
                // "Sin XML" se quitó: el estado "Pendiente XML" ya lo indica.
                render: (inv: SupplierInvoiceListItem) => (
                  <div className="flex flex-col gap-1 items-start">
                    <StatusBadge status={inv.status} />
                    <APAdjustmentBadge invoice={inv} />
                    {inv.status === 'approved' && inv.accounting_status === 'pending' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning-solid" />
                        Sin contabilizar
                      </span>
                    )}
                    {(inv.is_adjustment_orphan
                      || (inv.source === 'intl_invoice' && !inv.has_original_pdf && inv.status !== 'cancelled')
                      || inv.rcv_xml_mismatch) && (
                      <div className="flex items-center gap-1">
                        {inv.is_adjustment_orphan && (
                          <WarnChip tone="red" title="NC/ND huérfana: referencia una factura que no está en el sistema. Edita las referencias para corregir la asignación." />
                        )}
                        {inv.source === 'intl_invoice' && !inv.has_original_pdf && inv.status !== 'cancelled' && (
                          <WarnChip tone="amber" title="Factura internacional sin el PDF original adjunto." />
                        )}
                        {inv.rcv_xml_mismatch && (
                          <WarnChip tone="red" title="Descuadre RCV/XML: el XML difiere de los montos contabilizados desde el RCV. Revisar — no se recontabilizó automáticamente." />
                        )}
                      </div>
                    )}
                  </div>
                ),
              },
              { key: 'pago', header: 'Pago', render: (inv: SupplierInvoiceListItem) => <PaymentBadge invoice={inv} /> },
              { key: 'plazo', header: 'Plazo', render: (inv: SupplierInvoiceListItem) => <DeadlineBadge days={inv.days_until_deadline} /> },
              {
                key: 'total', header: 'Total', sortable: true, numeric: true,
                render: (inv: SupplierInvoiceListItem) => (
                  <span className="whitespace-nowrap font-medium text-content">
                    {(() => {
                      // Documentos con total 0 cuyo monto real va en MontoNF/MontoPeriodo
                      // (corredoras, servicios periódicos): mostrar el monto real, no $0.
                      const nf = parseFloat(inv.total) === 0 ? nonBillableAmount(inv.sii_montos) : null
                      if (nf) return (
                        <>
                          {fmtAmount(nf)}
                          <div className="text-[10px] text-content-subtle font-normal mt-0.5" title="Monto no facturable (no suma al total tributario)">No facturable</div>
                        </>
                      )
                      // Total tributario > 0 con monto no facturable (telecom): además del
                      // total afecto, mostrar el Valor a Pagar (afecto + no facturable).
                      const vp = payableTotal(inv.sii_montos, inv.total)
                      return (
                        <>
                          {fmtAmount(inv.total)}
                          {vp && (
                            <div className="text-[10px] text-warning font-normal mt-0.5" title="Valor a pagar (afecto + monto no facturable)">A pagar {fmtAmount(vp)}</div>
                          )}
                        </>
                      )
                    })()}
                    {inv.currency_original && inv.total_original && inv.currency_original !== 'CLP' && (
                      <div className="text-xs text-content-subtle font-normal mt-0.5">
                        {inv.currency_original} {parseFloat(inv.total_original).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )}
                  </span>
                ),
              },
              {
                key: 'acciones', header: 'Acciones',
                // El stopPropagation va acá dentro: la fila entera abre el detalle.
                render: (inv: SupplierInvoiceListItem) => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {['approved', 'paid'].includes(inv.status) && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiClient.get(`/purchases/invoices/${inv.id}/pdf/`, { responseType: 'blob' })
                            const url = URL.createObjectURL(res.data)
                            window.open(url, '_blank')
                          } catch { /* ignore */ }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface transition-colors"
                        title="Descargar PDF"
                      >
                        <FileDown size={11} />PDF
                      </button>
                    )}
                    {inv.status === 'registered' && canApprove && (
                      <button onClick={() => { setApproveFromDetail(false); setApproveTargetId(inv.id) }} disabled={approve.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface disabled:opacity-60">
                        <CheckCircle size={11} />Aprobar
                      </button>
                    )}
                    {/* Aprobar solo con RCV (sin XML) — habilitado por flag del tenant.
                        Etiqueta "Aprobar" a secas: el estado "Pendiente XML" ya indica
                        que no tiene XML, y el botón largo desarmaba la tabla. */}
                    {/* Sin XML (#1637): lo primero es responder al SII, como con
                        cualquier DTE recibido; aprobar sin XML pasa al menú. */}
                    {respondeSinXml(inv) && (
                      <button onClick={() => requestAcceptSii(null, `N° ${inv.invoice_number} de ${inv.supplier_name}`, inv.id)}
                        disabled={responderSinXml.isPending}
                        title="Acuse de recibo o aceptación ante el SII, sin esperar el XML"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60">
                        <CheckCircle size={11} />Responder al SII
                      </button>
                    )}
                    {['rcv_pending', 'rcv_reclamado'].includes(inv.status) && canApprove && allowApproveWithoutXml && !respondeSinXml(inv) && (
                      <button onClick={() => { setApproveFromDetail(false); setApproveTargetId(inv.id) }} disabled={approve.isPending}
                        title="Aprobar y contabilizar con los montos del RCV, sin esperar el XML"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface disabled:opacity-60">
                        <CheckCircle size={11} />Aprobar
                      </button>
                    )}
                    <KebabMenu items={[
                      { label: 'Editar montos', icon: <FilePenLine size={14} />, onClick: () => { setApiError(null); setEditIntlId(inv.id) }, hidden: inv.source !== 'intl_invoice' || !canEdit || inv.status === 'cancelled', disabled: updateIntlInvoice.isPending },
                      { label: 'Aprobar sin XML', icon: <CheckCircle size={14} />, onClick: () => { setApproveFromDetail(false); setApproveTargetId(inv.id) }, hidden: !respondeSinXml(inv) || inv.status !== 'rcv_pending' || !canApprove || !allowApproveWithoutXml, disabled: approve.isPending },
                      { label: 'Responder al SII', icon: <CheckCircle size={14} />, onClick: () => inv.sii_acuse && requestAcceptSii(inv.sii_acuse.inbound_dte_id, `N° ${inv.invoice_number} de ${inv.supplier_name}`, inv.id), hidden: !canAcuseSii || !inv.sii_acuse?.can_accept || respondeSinXml(inv), className: 'text-success-content hover:bg-success-surface', disabled: acuseSii.isPending || acceptSii.isPending },
                      { label: 'Reclamar ante el SII', icon: <XCircle size={14} />, onClick: () => inv.sii_acuse && requestRejectSii(inv.sii_acuse.inbound_dte_id, `N° ${inv.invoice_number} de ${inv.supplier_name}`, inv.id), hidden: !canAcuseSii || !inv.sii_acuse?.can_reject, className: 'text-danger-content-soft hover:bg-danger-surface', disabled: rejectSii.isPending || responderSinXml.isPending },
                      { label: 'Anular', icon: <XCircle size={14} />, onClick: () => requestCancel(inv.id, `N° ${inv.invoice_number} de ${inv.supplier_name}`), hidden: !['registered','approved'].includes(inv.status) || !canDelete, className: 'text-danger-content-soft hover:bg-danger-surface', disabled: cancelInvoice.isPending },
                      { label: 'Eliminar', icon: <Trash2 size={14} />, onClick: () => requestDelete(inv.id, `N° ${inv.invoice_number} de ${inv.supplier_name}`), hidden: inv.status === 'paid' || !canDelete, className: 'text-danger-content-soft hover:bg-danger-surface', disabled: deleteInvoice.isPending },
                    ]} />
                  </div>
                ),
              },
            ]}
            rows={data.invoices}
            rowKey={(inv: SupplierInvoiceListItem) => inv.id}
            onRowClick={(inv: SupplierInvoiceListItem) => openInvoice(inv.id)}
            {...ordenControlado(sort, handleSortChange)}
            surface={false}
            minWidth={1200}
            caption="Facturas de proveedor"
          />
        )}
      </div>

      <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
        <PageSizeSelector
          value={pageSize}
          onChange={n => { setPageSize(n); setPage(1) }}
          storageKey="supplier-invoices-page-size"
        />
        {data && data.pages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.count ?? 0}
            onPageChange={setPage}
            locale={locale}
          />
        )}
      </div>

      {modalOpen && (
        <SupplierInvoiceModal
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          isPending={createInvoice.isPending}
          error={apiError}
        />
      )}

      {editIntlId && editIntlInvoice && (
        <EditIntlInvoiceModal
          invoice={editIntlInvoice}
          onSave={handleSaveIntl}
          onClose={() => setEditIntlId(null)}
          isPending={updateIntlInvoice.isPending}
          error={apiError}
        />
      )}

      <SupplierInvoiceDetailModal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        invoice={detailInvoice ?? null}
        onApprove={id => { setApproveFromDetail(true); setApproveTargetId(id) }}
        onCancel={id => requestCancel(
          id,
          detailInvoice ? `N° ${detailInvoice.invoice_number} de ${detailInvoice.supplier_name}` : 'seleccionada',
          () => setDetailId(null),
        )}
        onDelete={canDelete ? (id => requestDelete(
          id,
          detailInvoice ? `N° ${detailInvoice.invoice_number} de ${detailInvoice.supplier_name}` : 'seleccionada',
          () => setDetailId(null),
        )) : undefined}
        approving={approve.isPending}
        cancelling={cancelInvoice.isPending}
        deleting={deleteInvoice.isPending}
        onAcceptSii={canAcuseSii && detailInvoice ? (inboundDteId => requestAcceptSii(
          inboundDteId,
          `N° ${detailInvoice.invoice_number} de ${detailInvoice.supplier_name}`,
          detailInvoice.id,
        )) : undefined}
        onRejectSii={canAcuseSii && detailInvoice ? (inboundDteId => requestRejectSii(
          inboundDteId,
          `N° ${detailInvoice.invoice_number} de ${detailInvoice.supplier_name}`,
          detailInvoice.id,
        )) : undefined}
        acceptingSii={acceptSii.isPending}
        rejectingSii={rejectSii.isPending}
        puedeCargarXml={canCreate}
        puedeAprobar={canApprove}
        aprobarSinXml={allowApproveWithoutXml}
      />

      <ApproveWithAccountModal
        open={!!approveTargetId}
        onClose={() => setApproveTargetId(null)}
        isPending={approve.isPending}
        title="Aprobar factura"
        description="Al aprobar se genera un comprobante contable en borrador. Por defecto se usa la cuenta del producto / proveedor / configuración. Se puede elegir otra cuenta de gasto si hace falta."
        confirmLabel="Aprobar"
        onConfirm={accountId => {
          if (!approveTargetId) return
          approve.mutate(
            { id: approveTargetId, accountId },
            {
              onSuccess: () => {
                setApproveTargetId(null)
                if (approveFromDetail) setDetailId(null)
              },
            },
          )
        }}
      />

      <ConfirmModal {...cancelModalProps} />
      <ConfirmModal {...deleteModalProps} />
      <AcuseSiiModal
        open={!!respuestaSii}
        onClose={() => setRespuestaSii(null)}
        onConfirm={confirmarRespuestaSii}
        isPending={acuseSii.isPending || acceptSii.isPending || rejectSii.isPending || responderSinXml.isPending}
        sinXml={respuestaSii?.id === null}
        modo={respuestaSii?.modo ?? 'aceptar'}
        documento={respuestaSii?.label ?? ''}
        recintoPorDefecto={companyProfile?.reception_venue ?? ''}
      />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
